import type { AgentSummary, MissionSummary } from "@/services/api/agentic-v2";

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

/**
 * Normalize a single assignee label from API fields that may embed a Mongo id
 * (e.g. ``68e3… · Guardy`` or a bare ``user_id``).
 */
export function formatAssigneeLabel(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const sep = trimmed.indexOf(" · ");
  if (sep > 0) {
    const left = trimmed.slice(0, sep).trim();
    const right = trimmed.slice(sep + 3).trim();
    if (OBJECT_ID_RE.test(left) && right) return right;
  }

  if (OBJECT_ID_RE.test(trimmed)) return null;
  return trimmed;
}

/**
 * Human-readable assignee summary for a mission row.
 *
 * Uses ``human_owner`` (advisor / owner string from the API) plus agent names
 * resolved from ``agents`` when ``assigned_agents`` ids are known.
 */
export function describeMissionAssignees(
  mission: MissionSummary,
  agents?: AgentSummary[] | null
): { text: string; unassigned: boolean } {
  const parts: string[] = [];

  const owner = mission.human_owner ? formatAssigneeLabel(mission.human_owner) : null;
  if (owner) parts.push(owner);

  const uniqueAgentIds = [...new Set(mission.assigned_agents)];
  const resolvedNames: string[] = [];
  for (const id of uniqueAgentIds) {
    const name = agents?.find((a) => a._id === id)?.name;
    if (name) resolvedNames.push(name);
  }

  if (resolvedNames.length > 0) {
    parts.push(...resolvedNames);
  } else if (uniqueAgentIds.length > 0) {
    parts.push(
      `${uniqueAgentIds.length} agent${uniqueAgentIds.length > 1 ? "s" : ""}`
    );
  }

  if (parts.length === 0) {
    return { text: "Unassigned", unassigned: true };
  }
  return { text: parts.join(" · "), unassigned: false };
}
